import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import styled from 'styled-components'

import {
  createInspectionTargetZone,
  InspectionMlLabel,
  deleteInspectionTargetZone,
  InspectionTargetZoneRecord,
  updateInspectionTargetZone,
} from '../../../../../api/inspectionTargetZones'
import { fetchZones, IZone } from '../../../../../api/zones'
import iconTrash from '../../../../../assets/images/icon_trash.svg'
import machineryGraphic from '../../../../../assets/images/inspection_machinery_multiangle.png'
import { useBuildingContext } from '../../../../../contexts/buildingContext'
import { useNotifications } from '../../../../../contexts/notificationProvider'
import { ViewSelector } from '../../../../common/ViewSelector/ViewSelector'
import { useInspectionTargetZonesQuery } from '../../hooks/useTargetZoneQueries'
import { IMapPoint } from '../ManagePoints/ManagedMapPoint'

type Rect = { left: number; top: number; width: number; height: number }

type DragState =
  | {
      mode: 'move'
      zoneId: number
      startX: number
      startY: number
      startRect: Rect
    }
  | {
      mode: 'resize'
      zoneId: number
      handle: 'nw' | 'ne' | 'sw' | 'se'
      startX: number
      startY: number
      startRect: Rect
    }
  | null

const MIN_RECT_SIZE = 10

const DEFAULT_ML_LABEL_STATUS: InspectionMlLabel['status'] = 'pass'
const DEFAULT_NEW_LABEL = 'new_label'

export const ManageTargetZones = () => {
  const { state: buildingState } = useBuildingContext()
  const { addNotification } = useNotifications()

  const [viewpointZones, setViewpointZones] = useState<IZone[]>([])
  const [loadingViewpointZones, setLoadingViewpointZones] = useState(true)
  const [activeViewpointZoneId, setActiveViewpointZoneId] = useState<
    number | null
  >(null)
  const [selectedTargetZoneId, setSelectedTargetZoneId] = useState<
    number | null
  >(null)
  const [isDrawMode, setIsDrawMode] = useState(false)
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(
    null,
  )
  const [draftRect, setDraftRect] = useState<Rect | null>(null)
  const [dragState, setDragState] = useState<DragState>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [zonesExpanded, setZonesExpanded] = useState(true)
  const [viewpointZonesExpanded, setViewpointZonesExpanded] = useState(true)
  const [detailsExpanded, setDetailsExpanded] = useState(true)
  const [imageMetrics, setImageMetrics] = useState<{
    naturalWidth: number
    naturalHeight: number
    displayWidth: number
    displayHeight: number
    offsetX: number
    offsetY: number
  } | null>(null)
  const [errorText, setErrorText] = useState<string | null>(null)
  const [pendingMlLabel, setPendingMlLabel] =
    useState<InspectionMlLabel | null>(null)

  const canvasRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const resizeRafRef = useRef<number | null>(null)

  useEffect(() => {
    let mounted = true
    const loadViewpointZones = async () => {
      try {
        setLoadingViewpointZones(true)
        const zones = await fetchZones(
          buildingState.projectId,
          buildingState.floorId,
        )
        if (!mounted) return
        setViewpointZones(zones)
        const firstZone = zones[0]?.id ?? null
        setActiveViewpointZoneId(current =>
          current != null && zones.some(zone => zone.id === current)
            ? current
            : firstZone,
        )
      } catch (_error) {
        if (!mounted) return
        setErrorText('Could not load viewpoint zones.')
      } finally {
        if (mounted) setLoadingViewpointZones(false)
      }
    }

    if (buildingState.projectId && buildingState.floorId) {
      loadViewpointZones()
    }

    return () => {
      mounted = false
    }
  }, [buildingState.floorId, buildingState.projectId])

  const targetZonesQuery = useInspectionTargetZonesQuery(
    buildingState.projectId,
    activeViewpointZoneId,
    !!activeViewpointZoneId,
  )

  const targetZones = useMemo(
    () => targetZonesQuery.data ?? [],
    [targetZonesQuery.data],
  )

  const selectedTargetZone: InspectionTargetZoneRecord | null = useMemo(() => {
    if (!selectedTargetZoneId) return null
    return targetZones.find(zone => zone.id === selectedTargetZoneId) ?? null
  }, [selectedTargetZoneId, targetZones])

  const activeViewpointZone = useMemo(
    () =>
      viewpointZones.find(zone => zone.id === activeViewpointZoneId) ?? null,
    [activeViewpointZoneId, viewpointZones],
  )

  const pointsBySubId = useMemo(() => {
    const points: IMapPoint[] = buildingState.floorData?.points ?? []
    const map = new Map<number, (typeof points)[number]>()
    points.forEach(point => {
      map.set(point.point_id, point)
    })
    return map
  }, [buildingState.floorData?.points])

  const pointsByDbId = useMemo(() => {
    const points: IMapPoint[] = buildingState.floorData?.points ?? []
    const map = new Map<number, IMapPoint>()
    points.forEach(point => {
      map.set(point.id, point)
    })
    return map
  }, [buildingState.floorData?.points])

  const availableViewpointSubIds = useMemo(() => {
    if (!activeViewpointZone) return []
    // Project floor zone memberships are usually stored as viewpoints_point DB IDs.
    // Resolve to sub_id (point_id) for target-zone viewpoint selection.
    const subIds = activeViewpointZone.point_ids
      .map(pointId => {
        const byDbId = pointsByDbId.get(pointId)
        if (byDbId) return byDbId.point_id
        const bySubId = pointsBySubId.get(pointId)
        if (bySubId) return bySubId.point_id
        return null
      })
      .filter((value): value is number => value !== null)
    return Array.from(new Set(subIds))
  }, [activeViewpointZone, pointsByDbId, pointsBySubId])

  const selectedViewpointSubId = useMemo(() => {
    if (
      selectedTargetZone &&
      availableViewpointSubIds.includes(selectedTargetZone.viewpoint_sub_id)
    ) {
      return selectedTargetZone.viewpoint_sub_id
    }
    return availableViewpointSubIds[0] ?? null
  }, [availableViewpointSubIds, selectedTargetZone])

  const selectedImageUrl = machineryGraphic

  useEffect(() => {
    if (!targetZones.length) {
      setSelectedTargetZoneId(null)
      return
    }
    setSelectedTargetZoneId(current => {
      if (current && targetZones.some(zone => zone.id === current)) {
        return current
      }
      return targetZones[0].id
    })
  }, [targetZones])

  useEffect(() => {
    setPendingMlLabel(null)
  }, [selectedTargetZoneId])

  const recalculateImageMetrics = useCallback(() => {
    const imageElement = imageRef.current
    const canvasElement = canvasRef.current
    if (!imageElement || !canvasElement) return
    if (imageElement.naturalWidth === 0 || imageElement.naturalHeight === 0)
      return

    const containerWidth = canvasElement.clientWidth
    const containerHeight = canvasElement.clientHeight
    const scale = Math.min(
      containerWidth / imageElement.naturalWidth,
      containerHeight / imageElement.naturalHeight,
    )
    const displayWidth = imageElement.naturalWidth * scale
    const displayHeight = imageElement.naturalHeight * scale
    const offsetX = (containerWidth - displayWidth) / 2
    // Canvas image is anchored to top-center (object-position: top center),
    // so overlays must use a top offset of zero to stay aligned.
    const offsetY = 0

    setImageMetrics(prev => {
      if (
        prev &&
        prev.naturalWidth === imageElement.naturalWidth &&
        prev.naturalHeight === imageElement.naturalHeight &&
        Math.abs(prev.displayWidth - displayWidth) < 0.5 &&
        Math.abs(prev.displayHeight - displayHeight) < 0.5 &&
        Math.abs(prev.offsetX - offsetX) < 0.5 &&
        Math.abs(prev.offsetY - offsetY) < 0.5
      ) {
        return prev
      }
      return {
        naturalWidth: imageElement.naturalWidth,
        naturalHeight: imageElement.naturalHeight,
        displayWidth,
        displayHeight,
        offsetX,
        offsetY,
      }
    })
  }, [])

  useEffect(() => {
    const element = canvasRef.current
    if (!element) return
    const observer = new ResizeObserver(() => {
      if (resizeRafRef.current !== null) {
        cancelAnimationFrame(resizeRafRef.current)
      }
      resizeRafRef.current = requestAnimationFrame(() => {
        resizeRafRef.current = null
        recalculateImageMetrics()
      })
    })
    observer.observe(element)
    return () => {
      observer.disconnect()
      if (resizeRafRef.current !== null) {
        cancelAnimationFrame(resizeRafRef.current)
        resizeRafRef.current = null
      }
    }
  }, [recalculateImageMetrics])

  const getRectForZone = (
    zone: InspectionTargetZoneRecord,
    metrics: NonNullable<typeof imageMetrics>,
  ): Rect => {
    return {
      left: metrics.offsetX + zone.overlay_x * metrics.displayWidth,
      top: metrics.offsetY + zone.overlay_y * metrics.displayHeight,
      width: zone.overlay_width * metrics.displayWidth,
      height: zone.overlay_height * metrics.displayHeight,
    }
  }

  const clampRectToImage = useCallback(
    (rect: Rect, metrics: NonNullable<typeof imageMetrics>): Rect => {
      const minLeft = metrics.offsetX
      const minTop = metrics.offsetY
      const maxRight = metrics.offsetX + metrics.displayWidth
      const maxBottom = metrics.offsetY + metrics.displayHeight

      const left = Math.max(
        minLeft,
        Math.min(rect.left, maxRight - MIN_RECT_SIZE),
      )
      const top = Math.max(
        minTop,
        Math.min(rect.top, maxBottom - MIN_RECT_SIZE),
      )
      let width = Math.max(MIN_RECT_SIZE, rect.width)
      let height = Math.max(MIN_RECT_SIZE, rect.height)

      if (left + width > maxRight) {
        width = maxRight - left
      }
      if (top + height > maxBottom) {
        height = maxBottom - top
      }

      return { left, top, width, height }
    },
    [],
  )

  const normalizeRect = useCallback(
    (rect: Rect) => {
      if (!imageMetrics) return null
      const x = (rect.left - imageMetrics.offsetX) / imageMetrics.displayWidth
      const y = (rect.top - imageMetrics.offsetY) / imageMetrics.displayHeight
      const width = rect.width / imageMetrics.displayWidth
      const height = rect.height / imageMetrics.displayHeight
      return { x, y, width, height }
    },
    [imageMetrics],
  )

  const saveZonePatch = useCallback(
    async (zoneId: number, patch: Partial<InspectionTargetZoneRecord>) => {
      if (!activeViewpointZoneId) return
      try {
        setIsSaving(true)
        await updateInspectionTargetZone(
          buildingState.projectId,
          zoneId,
          patch,
          activeViewpointZoneId,
        )
        await targetZonesQuery.refetch()
      } catch (_error) {
        addNotification('Could not save target zone changes', 'error')
      } finally {
        setIsSaving(false)
      }
    },
    [
      activeViewpointZoneId,
      addNotification,
      buildingState.projectId,
      targetZonesQuery,
    ],
  )

  const onCreateFromRect = async (rect: Rect): Promise<boolean> => {
    if (!selectedViewpointSubId || !activeViewpointZoneId) return false
    const normalized = normalizeRect(rect)
    if (!normalized) return false
    const displayOrder =
      targetZones.length > 0
        ? Math.max(...targetZones.map(zone => zone.display_order)) + 1
        : 1
    const name = `Target Zone ${targetZones.length + 1}`
    try {
      setIsSaving(true)
      const created = await createInspectionTargetZone(
        buildingState.projectId,
        {
          name,
          category: 'General',
          confidence_threshold: 0.5,
          ml_labels: [],
          overlay_x: normalized.x,
          overlay_y: normalized.y,
          overlay_width: normalized.width,
          overlay_height: normalized.height,
          viewpoint_sub_id: selectedViewpointSubId,
          display_order: displayOrder,
        },
        activeViewpointZoneId,
      )
      await targetZonesQuery.refetch()
      setSelectedTargetZoneId(created.id)
      addNotification('Target zone created', 'success')
      return true
    } catch (_error) {
      addNotification('Could not create target zone', 'error')
      return false
    } finally {
      setIsSaving(false)
    }
  }

  const onDeleteZone = async (zoneId: number) => {
    try {
      setIsSaving(true)
      await deleteInspectionTargetZone(buildingState.projectId, zoneId)
      await targetZonesQuery.refetch()
      addNotification('Target zone deleted', 'success')
    } catch (_error) {
      addNotification('Could not delete target zone', 'error')
    } finally {
      setIsSaving(false)
    }
  }

  const getPointerPosition = (event: MouseEvent | React.MouseEvent) => {
    if (!canvasRef.current) return null
    const rect = canvasRef.current.getBoundingClientRect()
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    }
  }

  const clampPointToImage = useCallback(
    (x: number, y: number) => {
      if (!imageMetrics) return { x, y }
      return {
        x: Math.max(
          imageMetrics.offsetX,
          Math.min(imageMetrics.offsetX + imageMetrics.displayWidth, x),
        ),
        y: Math.max(
          imageMetrics.offsetY,
          Math.min(imageMetrics.offsetY + imageMetrics.displayHeight, y),
        ),
      }
    },
    [imageMetrics],
  )

  const onCanvasClick = async (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isDrawMode || !imageMetrics || !selectedImageUrl) return
    const pointer = getPointerPosition(event)
    if (!pointer) return
    const clamped = clampPointToImage(pointer.x, pointer.y)

    if (!drawStart) {
      setDrawStart(clamped)
      setDraftRect({
        left: clamped.x,
        top: clamped.y,
        width: 1,
        height: 1,
      })
      return
    }

    const left = Math.min(drawStart.x, clamped.x)
    const top = Math.min(drawStart.y, clamped.y)
    const width = Math.abs(clamped.x - drawStart.x)
    const height = Math.abs(clamped.y - drawStart.y)
    const nextRect = clampRectToImage(
      { left, top, width, height },
      imageMetrics,
    )

    if (nextRect.width < MIN_RECT_SIZE || nextRect.height < MIN_RECT_SIZE) {
      addNotification(
        `Draw a larger box (minimum ${MIN_RECT_SIZE}px by ${MIN_RECT_SIZE}px).`,
        'warning',
      )
      setDraftRect(nextRect)
      return
    }

    const created = await onCreateFromRect(nextRect)
    if (created) {
      setDrawStart(null)
      setDraftRect(null)
      setIsDrawMode(false)
    }
  }

  const onCanvasMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isDrawMode || !drawStart || !imageMetrics) return
    const pointer = getPointerPosition(event)
    if (!pointer) return
    const left = Math.min(drawStart.x, pointer.x)
    const top = Math.min(drawStart.y, pointer.y)
    const width = Math.abs(pointer.x - drawStart.x)
    const height = Math.abs(pointer.y - drawStart.y)
    setDraftRect(clampRectToImage({ left, top, width, height }, imageMetrics))
  }

  const onStartDragZone = (
    event: React.MouseEvent<HTMLDivElement>,
    zone: InspectionTargetZoneRecord,
  ) => {
    if (!imageMetrics || isDrawMode) return
    event.stopPropagation()
    const pointer = getPointerPosition(event)
    if (!pointer) return
    setDragState({
      mode: 'move',
      zoneId: zone.id,
      startX: pointer.x,
      startY: pointer.y,
      startRect: getRectForZone(zone, imageMetrics),
    })
  }

  const onStartResizeZone = (
    event: React.MouseEvent<HTMLDivElement>,
    zone: InspectionTargetZoneRecord,
    handle: 'nw' | 'ne' | 'sw' | 'se',
  ) => {
    if (!imageMetrics || isDrawMode) return
    event.stopPropagation()
    const pointer = getPointerPosition(event)
    if (!pointer) return
    setDragState({
      mode: 'resize',
      zoneId: zone.id,
      handle,
      startX: pointer.x,
      startY: pointer.y,
      startRect: getRectForZone(zone, imageMetrics),
    })
  }

  useEffect(() => {
    if (!dragState || !imageMetrics) return
    const onMove = (event: MouseEvent) => {
      const pointer = getPointerPosition(event)
      if (!pointer) return
      const dx = pointer.x - dragState.startX
      const dy = pointer.y - dragState.startY
      let nextRect: Rect = dragState.startRect

      if (dragState.mode === 'move') {
        nextRect = {
          left: dragState.startRect.left + dx,
          top: dragState.startRect.top + dy,
          width: dragState.startRect.width,
          height: dragState.startRect.height,
        }
      } else {
        const right = dragState.startRect.left + dragState.startRect.width
        const bottom = dragState.startRect.top + dragState.startRect.height
        let left = dragState.startRect.left
        let top = dragState.startRect.top
        let width = dragState.startRect.width
        let height = dragState.startRect.height

        if (dragState.handle.includes('w')) {
          left = dragState.startRect.left + dx
          width = right - left
        }
        if (dragState.handle.includes('e')) {
          width = dragState.startRect.width + dx
        }
        if (dragState.handle.includes('n')) {
          top = dragState.startRect.top + dy
          height = bottom - top
        }
        if (dragState.handle.includes('s')) {
          height = dragState.startRect.height + dy
        }

        nextRect = { left, top, width, height }
      }

      setDraftRect(clampRectToImage(nextRect, imageMetrics))
    }

    const onUp = async () => {
      if (!draftRect) {
        setDragState(null)
        return
      }
      const normalized = normalizeRect(draftRect)
      if (!normalized) return
      await saveZonePatch(dragState.zoneId, {
        overlay_x: normalized.x,
        overlay_y: normalized.y,
        overlay_width: normalized.width,
        overlay_height: normalized.height,
      })
      setDraftRect(null)
      setDragState(null)
    }

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [
    clampRectToImage,
    dragState,
    draftRect,
    imageMetrics,
    normalizeRect,
    saveZonePatch,
  ])

  const displayedZones = useMemo(() => {
    return targetZones.sort((a, b) => a.display_order - b.display_order)
  }, [targetZones])

  const saveField =
    <K extends keyof InspectionTargetZoneRecord>(
      field: K,
      value: InspectionTargetZoneRecord[K],
    ) =>
    async () => {
      if (!selectedTargetZone) return
      await saveZonePatch(selectedTargetZone.id, { [field]: value })
    }

  const saveMlLabels = useCallback(
    async (nextMlLabels: InspectionMlLabel[]) => {
      if (!selectedTargetZone) return
      await saveZonePatch(selectedTargetZone.id, { ml_labels: nextMlLabels })
    },
    [saveZonePatch, selectedTargetZone],
  )

  const createPendingMlLabel = useCallback(async () => {
    if (!selectedTargetZone || !pendingMlLabel) return
    const normalizedLabel = pendingMlLabel.label.trim()
    if (!normalizedLabel) {
      addNotification('Enter a label before creating it.', 'warning')
      return
    }
    const nextMlLabels = [
      ...(selectedTargetZone.ml_labels ?? []),
      {
        label: normalizedLabel,
        status: pendingMlLabel.status,
      },
    ]
    await saveMlLabels(nextMlLabels)
    setPendingMlLabel(null)
  }, [addNotification, pendingMlLabel, saveMlLabels, selectedTargetZone])

  return (
    <Layout>
      <CanvasSection>
        <CanvasHeader>
          <strong>
            {activeViewpointZone
              ? `Viewpoint Zone: ${activeViewpointZone.name}`
              : 'Select a viewpoint zone'}
          </strong>
        </CanvasHeader>
        <Canvas
          ref={canvasRef}
          onClick={onCanvasClick}
          onMouseMove={onCanvasMouseMove}
          $drawMode={isDrawMode}
        >
          {!selectedViewpointSubId && (
            <EmptyState>
              Select a viewpoint zone and viewpoint to edit overlays.
            </EmptyState>
          )}
          <CanvasImage
            ref={imageRef}
            src={selectedImageUrl}
            alt="Inspection machine multi-angle layout"
            onLoad={recalculateImageMetrics}
          />
          {imageMetrics &&
            displayedZones.map(zone => {
              const isSelected = selectedTargetZoneId === zone.id
              const rect =
                draftRect && dragState?.zoneId === zone.id
                  ? draftRect
                  : getRectForZone(zone, imageMetrics)
              return (
                <ZoneOverlay
                  key={zone.id}
                  style={{
                    left: rect.left,
                    top: rect.top,
                    width: rect.width,
                    height: rect.height,
                    pointerEvents: isDrawMode ? 'none' : 'auto',
                  }}
                  $selected={isSelected}
                  onClick={event => {
                    event.stopPropagation()
                    setSelectedTargetZoneId(zone.id)
                  }}
                  onMouseDown={event => onStartDragZone(event, zone)}
                >
                  <ZoneLabel>{zone.name || `Zone ${zone.id}`}</ZoneLabel>
                  {isSelected && (
                    <>
                      {(['nw', 'ne', 'sw', 'se'] as const).map(handle => (
                        <ResizeHandle
                          key={handle}
                          $handle={handle}
                          onMouseDown={event =>
                            onStartResizeZone(event, zone, handle)
                          }
                        />
                      ))}
                    </>
                  )}
                </ZoneOverlay>
              )
            })}
          {draftRect &&
            (!dragState || dragState.mode === 'move' || isDrawMode) && (
              <DraftZone
                style={{
                  left: draftRect.left,
                  top: draftRect.top,
                  width: draftRect.width,
                  height: draftRect.height,
                }}
              />
            )}
        </Canvas>
      </CanvasSection>

      <Sidebar>
        <ViewSelector
          title="Viewpoint Zones"
          initialViewSelectorExpanded
          viewingExpanded={viewpointZonesExpanded}
          setViewingExpanded={setViewpointZonesExpanded}
          maxHeight={240}
        >
          <ListSection>
            {loadingViewpointZones && <ListItem>Loading...</ListItem>}
            {!loadingViewpointZones &&
              viewpointZones.map(zone => (
                <ListItem
                  key={zone.id}
                  $active={zone.id === activeViewpointZoneId}
                  onClick={() => setActiveViewpointZoneId(zone.id)}
                >
                  <span>{zone.name}</span>
                  <MetaText>{zone.point_ids.length} viewpoints</MetaText>
                </ListItem>
              ))}
            {!loadingViewpointZones && viewpointZones.length === 0 && (
              <ListItem>No viewpoint zones found for this floor.</ListItem>
            )}
          </ListSection>
        </ViewSelector>

        <ViewSelector
          title="Target Zones"
          initialViewSelectorExpanded
          viewingExpanded={zonesExpanded}
          setViewingExpanded={setZonesExpanded}
          maxHeight={300}
        >
          <ListSection>
            <AddTargetZoneButton
              type="button"
              onClick={() => setIsDrawMode(value => !value)}
              disabled={!selectedViewpointSubId}
            >
              {isDrawMode ? 'Cancel Draw' : '+ Add Target Zone'}
            </AddTargetZoneButton>
            {targetZonesQuery.isLoading && <ListItem>Loading...</ListItem>}
            {displayedZones.map(zone => (
              <ListItem
                key={zone.id}
                $active={zone.id === selectedTargetZoneId}
                onClick={() => setSelectedTargetZoneId(zone.id)}
              >
                <span>{zone.name || `Zone ${zone.id}`}</span>
                <DeleteIcon
                  src={iconTrash}
                  alt="Delete"
                  onClick={event => {
                    event.stopPropagation()
                    onDeleteZone(zone.id)
                  }}
                />
              </ListItem>
            ))}
            {displayedZones.length === 0 && !targetZonesQuery.isLoading && (
              <ListItem>No target zones for selected viewpoint.</ListItem>
            )}
          </ListSection>
        </ViewSelector>

        <ViewSelector
          title="Target Zone Details"
          initialViewSelectorExpanded
          viewingExpanded={detailsExpanded}
          setViewingExpanded={setDetailsExpanded}
          maxHeight={420}
        >
          <DetailsSection>
            {!selectedTargetZone && (
              <MetaText>Select a target zone to edit details.</MetaText>
            )}
            {selectedTargetZone && (
              <>
                <FieldLabel>Name</FieldLabel>
                <TextInput
                  key={`name-${selectedTargetZone.id}`}
                  defaultValue={selectedTargetZone.name}
                  onBlur={event => saveField('name', event.target.value)()}
                />

                <FieldLabel>Category</FieldLabel>
                <TextInput
                  key={`category-${selectedTargetZone.id}`}
                  defaultValue={selectedTargetZone.category}
                  onBlur={event => saveField('category', event.target.value)()}
                />

                <FieldLabel>Viewpoint</FieldLabel>
                <SelectInput
                  value={selectedTargetZone.viewpoint_sub_id}
                  onChange={event =>
                    saveField(
                      'viewpoint_sub_id',
                      parseInt(event.target.value, 10),
                    )()
                  }
                >
                  {availableViewpointSubIds.map(subId => (
                    <option key={subId} value={subId}>
                      Viewpoint {subId}
                    </option>
                  ))}
                </SelectInput>

                <FieldLabel>Confidence Threshold</FieldLabel>
                <TextInput
                  key={`confidence-${selectedTargetZone.id}`}
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  defaultValue={selectedTargetZone.confidence_threshold}
                  onBlur={event =>
                    saveField(
                      'confidence_threshold',
                      parseFloat(event.target.value || '0'),
                    )()
                  }
                />

                <FieldLabel>Display Order</FieldLabel>
                <TextInput
                  key={`display-order-${selectedTargetZone.id}`}
                  type="number"
                  min="0"
                  defaultValue={selectedTargetZone.display_order}
                  onBlur={event =>
                    saveField(
                      'display_order',
                      parseInt(event.target.value, 10) || 0,
                    )()
                  }
                />

                <FieldLabel>ML Labels</FieldLabel>
                <MlLabelsContainer>
                  {(selectedTargetZone.ml_labels ?? []).map((ml, index) => (
                    <MlLabelCard key={`${selectedTargetZone.id}-${index}`}>
                      <MlLabelRow>
                        <TextInput
                          defaultValue={ml.label}
                          placeholder="Label"
                          onBlur={event => {
                            const current = [
                              ...(selectedTargetZone.ml_labels ?? []),
                            ]
                            current[index] = {
                              ...current[index],
                              label: event.target.value.trim(),
                            }
                            saveMlLabels(
                              current.filter(
                                item => item.label.trim().length > 0,
                              ),
                            )
                          }}
                        />
                        <MlLabelActions>
                          <CompactSelectInput
                            value={ml.status}
                            onChange={event => {
                              const current = [
                                ...(selectedTargetZone.ml_labels ?? []),
                              ]
                              current[index] = {
                                ...current[index],
                                label:
                                  current[index].label || DEFAULT_NEW_LABEL,
                                status: event.target
                                  .value as InspectionMlLabel['status'],
                              }
                              saveMlLabels(current)
                            }}
                          >
                            <option value="pass">pass</option>
                            <option value="fail">fail</option>
                            <option value="warn">warn</option>
                          </CompactSelectInput>
                          <DeleteTextButton
                            type="button"
                            onClick={() => {
                              const current = [
                                ...(selectedTargetZone.ml_labels ?? []),
                              ]
                              current.splice(index, 1)
                              saveMlLabels(current)
                            }}
                          >
                            Remove
                          </DeleteTextButton>
                        </MlLabelActions>
                      </MlLabelRow>
                    </MlLabelCard>
                  ))}
                  {pendingMlLabel && (
                    <MlLabelCard>
                      <MlLabelRow>
                        <TextInput
                          value={pendingMlLabel.label}
                          placeholder="Label"
                          onChange={event =>
                            setPendingMlLabel(current =>
                              current
                                ? { ...current, label: event.target.value }
                                : current,
                            )
                          }
                        />
                        <MlLabelActions>
                          <CompactSelectInput
                            value={pendingMlLabel.status}
                            onChange={event =>
                              setPendingMlLabel(current =>
                                current
                                  ? {
                                      ...current,
                                      status: event.target
                                        .value as InspectionMlLabel['status'],
                                    }
                                  : current,
                              )
                            }
                          >
                            <option value="pass">pass</option>
                            <option value="fail">fail</option>
                            <option value="warn">warn</option>
                          </CompactSelectInput>
                          <CreateTextButton
                            type="button"
                            onClick={createPendingMlLabel}
                          >
                            Create
                          </CreateTextButton>
                        </MlLabelActions>
                      </MlLabelRow>
                    </MlLabelCard>
                  )}
                  <AddMlLabelButton
                    type="button"
                    disabled={!!pendingMlLabel}
                    onClick={() =>
                      setPendingMlLabel({
                        label: '',
                        status: DEFAULT_ML_LABEL_STATUS,
                      })
                    }
                  >
                    Add Label
                  </AddMlLabelButton>
                </MlLabelsContainer>
              </>
            )}
            {(isSaving || targetZonesQuery.isFetching) && (
              <MetaText>Saving changes...</MetaText>
            )}
            {errorText && <ErrorText>{errorText}</ErrorText>}
          </DetailsSection>
        </ViewSelector>
      </Sidebar>
    </Layout>
  )
}

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 16px;
  min-height: 600px;
  max-height: calc(100vh - 260px);
`

const CanvasSection = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`

const CanvasHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #4a5568;
  font-size: 14px;
`

const DrawButton = styled.button`
  background: #ffffff;
  border: 1px solid #073c7a;
  border-radius: 4px;
  color: #073c7a;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  font-size: 14px;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`

const Canvas = styled.div<{ $drawMode: boolean }>`
  position: relative;
  background: #f8f8f8;
  border-radius: 8px;
  overflow: hidden;
  flex: 1;
  min-height: 520px;
  cursor: ${props => (props.$drawMode ? 'crosshair' : 'default')};
`

const CanvasImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: top center;
  user-select: none;
`

const EmptyState = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cbd5e1;
  font-size: 14px;
`

const ZoneOverlay = styled.div<{ $selected: boolean }>`
  position: absolute;
  border: 2px solid ${props => (props.$selected ? '#f472b6' : '#38bdf8')};
  background: ${props =>
    props.$selected ? 'rgba(244,114,182,0.16)' : 'rgba(56,189,248,0.12)'};
`

const DraftZone = styled.div`
  position: absolute;
  border: 2px dashed #facc15;
  background: rgba(250, 204, 21, 0.18);
  pointer-events: none;
`

const ZoneLabel = styled.div`
  position: absolute;
  top: -20px;
  left: 0;
  background: rgba(15, 23, 42, 0.85);
  color: #f8fafc;
  font-size: 11px;
  line-height: 1;
  padding: 4px 6px;
  border-radius: 4px;
`

const ResizeHandle = styled.div<{ $handle: 'nw' | 'ne' | 'sw' | 'se' }>`
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #f8fafc;
  border: 1px solid #334155;
  cursor: ${props => {
    if (props.$handle === 'nw' || props.$handle === 'se') return 'nwse-resize'
    return 'nesw-resize'
  }};
  ${props => (props.$handle.includes('n') ? 'top: -5px;' : 'bottom: -5px;')}
  ${props => (props.$handle.includes('w') ? 'left: -5px;' : 'right: -5px;')}
`

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
`

const ListSection = styled.div`
  padding-top: 8px;
`

const AddTargetZoneButton = styled(DrawButton)`
  width: 100%;
  margin-bottom: 10px;
`

const ListItem = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 6px;
  cursor: pointer;
  border-bottom: 1px solid #e2e8f0;
  background: ${props => (props.$active ? '#ecfeff' : 'transparent')};
  color: #334155;
  font-size: 13px;
`

const DeleteIcon = styled.img`
  width: 14px;
  height: 14px;
  opacity: 0.6;
  cursor: pointer;
`

const DetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
`

const FieldLabel = styled.label`
  color: #64748b;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const TextInput = styled.input`
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 8px;
  font-size: 13px;
`

const SelectInput = styled.select`
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 8px;
  font-size: 13px;
`

const MetaText = styled.div`
  color: #64748b;
  font-size: 12px;
`

const ErrorText = styled.div`
  color: #b91c1c;
  font-size: 12px;
`

const MlLabelsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const MlLabelCard = styled.div`
  background: #f8fafc;
  border: 1px solid #dbe3ee;
  border-radius: 8px;
  padding: 8px;
`

const MlLabelRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const MlLabelActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const AddMlLabelButton = styled.button`
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 12px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const DeleteTextButton = styled.button`
  border: 1px solid #fecaca;
  background: #fff5f5;
  color: #dc2626;
  font-size: 12px;
  cursor: pointer;
  border-radius: 4px;
  padding: 6px 8px;
  white-space: nowrap;
`

const CreateTextButton = styled.button`
  border: 1px solid #bbf7d0;
  background: #f0fdf4;
  color: #166534;
  font-size: 12px;
  cursor: pointer;
  border-radius: 4px;
  padding: 6px 8px;
  white-space: nowrap;
`

const CompactSelectInput = styled.select`
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 12px;
  min-width: 90px;
`
